import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileText, CheckSquare, Lightning, ShareNetwork, FloppyDisk, SpinnerGap, ArrowLeft, WhatsappLogo } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const APP_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_LABELS = {
  sheet: { label: "Ficha Técnica", icon: FileText, color: "text-emerald-700", bg: "bg-emerald-50" },
  checklist: { label: "Checklist", icon: CheckSquare, color: "text-amber-700", bg: "bg-amber-50" },
  attack_plan: { label: "Plano de Ataque", icon: Lightning, color: "text-violet-700", bg: "bg-violet-50" },
};

export default function SharedItemView() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [shared, setShared] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchShared = async () => {
      try {
        const { data } = await axios.get(`${API}/share/${code}`);
        setShared(data);
      } catch {
        setError("Link não encontrado ou expirado");
      } finally {
        setLoading(false);
      }
    };
    fetchShared();
  }, [code]);

  const handleSave = async () => {
    if (!user) {
      toast.error("Faça login para salvar na sua conta");
      navigate("/login");
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/share/${code}/save`);
      toast.success(data.message);
      navigate(data.item_type === "sheet" ? "/technical-sheets" : data.item_type === "checklist" ? "/checklists" : "/attack-plan");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const shareUrl = `${APP_URL}/share/${code}`;
  const whatsappUrl = shared ? `https://wa.me/?text=${encodeURIComponent(`${shared.shared_by_name} compartilhou ${TYPE_LABELS[shared.type]?.label || 'um item'}: "${shared.item_data?.name || ''}" — ${shareUrl}`)}` : "";

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen bg-zinc-50">
      <SpinnerGap size={32} className="animate-spin text-zinc-400" />
    </div>
  );

  if (error) return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-zinc-50 p-6">
      <ShareNetwork size={48} className="text-zinc-300 mb-4" />
      <h2 className="text-xl font-bold text-zinc-800">{error}</h2>
      <p className="text-sm text-zinc-500 mt-2">O link pode ter expirado ou sido removido.</p>
      <Button onClick={() => navigate("/login")} className="mt-6" variant="outline">
        <ArrowLeft size={16} className="mr-2" /> Ir para o app
      </Button>
    </div>
  );

  const config = TYPE_LABELS[shared.type] || TYPE_LABELS.sheet;
  const Icon = config.icon;
  const item = shared.item_data || {};

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4" data-testid="shared-item-page">
      <div className="w-full max-w-lg">
        <div className="text-center mb-6">
          <div className={`w-14 h-14 rounded-2xl ${config.bg} flex items-center justify-center mx-auto mb-3`}>
            <Icon size={28} className={config.color} weight="fill" />
          </div>
          <p className="text-xs text-zinc-400 uppercase tracking-wider font-bold">{config.label} Compartilhada</p>
          {shared.shared_by_name && (
            <p className="text-xs text-zinc-500 mt-1">
              Compartilhado por <span className="font-semibold">{shared.shared_by_name}</span>
              {shared.company_name ? ` · ${shared.company_name}` : ""}
            </p>
          )}
        </div>

        <Card className="border-zinc-200 shadow-lg">
          <CardContent className="p-5">
            <h2 className="text-lg font-bold text-zinc-900 mb-4" data-testid="shared-item-name">{item.name || "Sem nome"}</h2>

            {/* Sheet details */}
            {shared.type === "sheet" && (
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="bg-zinc-50 rounded-lg p-2">
                    <p className="text-[10px] text-zinc-400 uppercase">Rendimento</p>
                    <p className="text-sm font-bold text-zinc-800">{item.yield_quantity} {item.yield_unit}</p>
                  </div>
                  <div className="bg-zinc-50 rounded-lg p-2">
                    <p className="text-[10px] text-zinc-400 uppercase">Custo Total</p>
                    <p className="text-sm font-bold text-zinc-800">R$ {(item.total_cost || 0).toFixed(2)}</p>
                  </div>
                  <div className="bg-emerald-50 rounded-lg p-2">
                    <p className="text-[10px] text-emerald-500 uppercase">Custo/Un</p>
                    <p className="text-sm font-bold text-emerald-700">R$ {(item.cost_per_unit || 0).toFixed(2)}</p>
                  </div>
                </div>
                {item.ingredients && item.ingredients.length > 0 && (
                  <div>
                    <p className="text-xs font-bold text-zinc-700 uppercase tracking-wider mb-2">Ingredientes ({item.ingredients.length})</p>
                    <div className="space-y-1">
                      {item.ingredients.map((ing, i) => (
                        <div key={`ing-${ing.ingredient_id || i}-${i}`} className="flex items-center justify-between text-xs bg-zinc-50 rounded px-2 py-1.5">
                          <span className="text-zinc-700 font-medium">{ing.ingredient_name}</span>
                          <span className="text-zinc-500">{ing.quantidade_bruta || ing.quantity_used} {ing.unit}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {item.modo_preparo && (
                  <div>
                    <p className="text-xs font-bold text-zinc-700 uppercase tracking-wider mb-1">Modo de Preparo</p>
                    <p className="text-xs text-zinc-600 whitespace-pre-wrap leading-relaxed">{item.modo_preparo}</p>
                  </div>
                )}
              </div>
            )}

            {/* Checklist details */}
            {shared.type === "checklist" && item.items && (
              <div className="space-y-1">
                {item.items.map((it, i) => (
                  <div key={`ck-${i}-${(it.text || '').slice(0, 20)}`} className="flex items-center gap-2 text-sm text-zinc-700 bg-zinc-50 rounded px-2 py-1.5">
                    <div className="w-4 h-4 rounded border border-zinc-300 flex-shrink-0" />
                    <span>{it.text}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Attack Plan details */}
            {shared.type === "attack_plan" && (
              <div className="text-sm text-zinc-600">
                <p>{item.description || "Plano de produção compartilhado"}</p>
                {item.phases && item.phases.map((phase, i) => (
                  <div key={`ph-${i}-${phase.name || ''}`} className="mt-2 bg-zinc-50 rounded p-2">
                    <p className="font-bold text-xs text-zinc-800">{phase.name}</p>
                    {phase.tasks?.map((task, j) => (
                      <p key={`task-${i}-${j}`} className="text-xs text-zinc-600 ml-3">• {task.text || task}</p>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex gap-2 mt-4">
          <Button onClick={handleSave} disabled={saving} className="flex-1 bg-zinc-950 text-white hover:bg-zinc-800 h-11" data-testid="save-shared-item-btn">
            {saving ? <SpinnerGap size={16} className="animate-spin mr-2" /> : <FloppyDisk size={16} className="mr-2" />}
            {user ? "Salvar na Minha Conta" : "Fazer Login para Salvar"}
          </Button>
          <Button variant="outline" asChild className="h-11 border-green-300 text-green-700 hover:bg-green-50">
            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" data-testid="share-whatsapp-btn">
              <WhatsappLogo size={18} weight="fill" />
            </a>
          </Button>
        </div>
      </div>
    </div>
  );
}
